import React, { useState, useEffect } from "react";
import { api, resolveImg } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, Upload, LogOut, Pencil, ChevronLeft, ChevronRight, X } from "lucide-react";

const empty = { title_sk: "", title_en: "", year: "", medium_sk: "Kombinovaná technika", medium_en: "Mixed media", size: "80 × 50 cm", description_sk: "", description_en: "", category: "vystava1", image_url: "" };

const Admin = () => {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [paintings, setPaintings] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/paintings").then((r) => setPaintings(r.data)).catch(() => {});

  useEffect(() => {
    const token = localStorage.getItem("mh_token");
    const tryGoogle = () =>
      api.get("/auth/google/me")
        .then((r) => { if (r.data.is_admin) { setAuthed(true); load(); } })
        .catch(() => {})
        .finally(() => setChecking(false));
    if (token) {
      api.get("/auth/me")
        .then(() => { setAuthed(true); load(); setChecking(false); })
        .catch(() => { localStorage.removeItem("mh_token"); tryGoogle(); });
    } else {
      tryGoogle();
    }
  }, []);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/login", creds);
      localStorage.setItem("mh_token", data.token);
      setAuthed(true);
      load();
      toast.success("Prihlásené / Logged in");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
  };

  const logout = async () => {
    localStorage.removeItem("mh_token");
    // End both auth paths server-side so refreshing the page cannot silently
    // re-authenticate through a lingering cookie (JWT access_token or Google session_token).
    try { await api.post("/auth/logout"); } catch (e) { /* best-effort */ }
    try { await api.post("/auth/google/logout"); } catch (e) { /* best-effort */ }
    setAuthed(false);
  };

  // Client-side downscale + JPEG compression so large phone photos survive
  // ingress/body-size limits and upload consistently on production.
  const compressImage = (file) =>
    new Promise((resolve, reject) => {
      // Only try to compress raster images; leave everything else untouched.
      if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) return resolve(file);
      const img = new Image();
      const reader = new FileReader();
      reader.onload = () => { img.src = reader.result; };
      reader.onerror = () => resolve(file);
      img.onload = () => {
        const MAX = 2000; // longest edge
        let { width, height } = img;
        const scale = Math.min(1, MAX / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return resolve(file);
            const out = new File([blob], (file.name || "image").replace(/\.(png|webp|jpe?g)$/i, "") + ".jpg", { type: "image/jpeg" });
            resolve(out.size < file.size ? out : file);
          },
          "image/jpeg",
          0.86
        );
      };
      img.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });

  const onUpload = async (e) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    setUploading(true);
    try {
      const file = await compressImage(raw);
      const fd = new FormData();
      fd.append("file", file);
      // Let axios set the multipart boundary automatically — do NOT hardcode Content-Type.
      const { data } = await api.post("/paintings/upload", fd);
      setForm((f) => ({ ...f, image_url: data.image_url }));
      toast.success("Obrázok nahraný / Image uploaded");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.statusText || err?.message || "Upload failed";
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setUploading(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({ title_sk: p.title_sk, title_en: p.title_en, year: p.year, medium_sk: p.medium_sk || "Kombinovaná technika", medium_en: p.medium_en || "Mixed media", size: p.size || "80 × 50 cm", description_sk: p.description_sk || "", description_en: p.description_en || "", category: p.category, image_url: p.image_url });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditingId(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.image_url) return toast.error("Najprv nahrajte obrázok / Upload an image first");
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/paintings/${editingId}`, form);
        toast.success("Uložené / Saved");
      } else {
        await api.post("/paintings", form);
        toast.success("Dielo pridané / Work added");
      }
      cancelEdit();
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.statusText || err?.message || "Save failed";
      toast.error(`Save failed: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Odstrániť dielo? / Delete work?")) return;
    await api.delete(`/paintings/${id}`);
    if (editingId === id) cancelEdit();
    load();
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= paintings.length) return;
    const arr = [...paintings];
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setPaintings(arr);
    try {
      await api.post("/paintings/reorder", { ids: arr.map((p) => p.id) });
    } catch (err) {
      toast.error("Reorder failed");
      load();
    }
  };

  if (checking) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#8d8a83]"><Loader2 className="animate-spin" /></div>;

  if (!authed)
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <form onSubmit={login} className="w-full max-w-sm" data-testid="admin-login-form">
          <h1 className="font-serif-display text-4xl text-[#f5f2eb] mb-2">Admin</h1>
          <p className="text-[#8d8a83] mb-8 font-light text-sm">Marián Hriňa — Studio</p>
          <input data-testid="admin-email" className="field mb-6" placeholder="Email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} />
          <input data-testid="admin-password" type="password" className="field mb-8" placeholder="Heslo / Password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} />
          <button data-testid="admin-login-button" className="w-full bg-[#f5f2eb] text-[#050505] py-3 overline rounded-full hover:bg-white transition-colors">Prihlásiť / Sign in</button>
        </form>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#050505] text-[#f5f2eb] px-6 md:px-10 py-12">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center justify-between mb-12">
          <h1 className="font-serif-display text-4xl">Štúdio — Galéria</h1>
          <button data-testid="admin-logout" onClick={logout} className="overline text-[#8d8a83] hover:text-[#f5f2eb] flex items-center gap-2"><LogOut size={16} /> Odhlásiť</button>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <form onSubmit={save} className="space-y-6 lg:sticky lg:top-12 self-start" data-testid="painting-form">
            <div className="flex items-center justify-between">
              <h2 className="overline text-[#b02418]">{editingId ? "Upraviť dielo / Edit work" : "Pridať dielo / Add work"}</h2>
              {editingId && (
                <button type="button" onClick={cancelEdit} className="overline text-[#8d8a83] hover:text-[#f5f2eb] flex items-center gap-1" data-testid="cancel-edit">
                  <X size={14} /> Zrušiť
                </button>
              )}
            </div>
            <label className="block border border-dashed border-white/20 rounded-lg p-8 text-center cursor-pointer hover:border-[#b02418] transition-colors">
              <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="painting-image-input" />
              {uploading ? (
                <span className="flex items-center justify-center gap-2 text-[#8d8a83]"><Loader2 size={16} className="animate-spin" /> Nahrávam…</span>
              ) : form.image_url ? (
                <img src={resolveImg(form.image_url)} alt="preview" className="max-h-48 mx-auto" />
              ) : (
                <span className="flex items-center justify-center gap-2 text-[#8d8a83]"><Upload size={18} /> Nahrať obrázok / Upload image</span>
              )}
            </label>
            <div className="grid grid-cols-2 gap-4">
              <input className="field" placeholder="Názov (SK)" value={form.title_sk} onChange={(e) => setForm({ ...form, title_sk: e.target.value })} data-testid="painting-title-sk" />
              <input className="field" placeholder="Title (EN)" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} data-testid="painting-title-en" />
              <input className="field" placeholder="Rok / Year" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="painting-year" />
              <input className="field" placeholder="Rozmer / Size (napr. 80 × 50 cm)" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} data-testid="painting-size" />
              <select className="field col-span-2" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="painting-category">
                <option value="vystava1" className="bg-[#0a0a0a]">Výstava ArtGate</option>
                <option value="vystava2" className="bg-[#0a0a0a]">Výstava JE-DNA</option>
                <option value="ina" className="bg-[#0a0a0a]">Iné / Other (len v „Všetko")</option>
              </select>
            </div>
            <button disabled={saving} className="bg-[#f5f2eb] text-[#050505] px-8 py-3 overline rounded-full hover:bg-white transition-colors disabled:opacity-60" data-testid="painting-save-button">
              {saving ? "Ukladám…" : editingId ? "Uložiť zmeny" : "Pridať dielo"}
            </button>
          </form>

          <div>
            <h2 className="overline text-[#b02418] mb-2">Diela ({paintings.length})</h2>
            <p className="text-xs text-[#8d8a83] mb-6 font-light">Šípky menia poradie • ceruzka upraví názov • kôš odstráni / Arrows reorder • pencil renames • trash deletes</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {paintings.map((p, i) => (
                <div key={p.id} className={`relative group ${editingId === p.id ? "ring-2 ring-[#b02418]" : ""}`} data-testid={`admin-painting-${p.id}`}>
                  {p.is_missing ? (
                    <div className="w-full h-32 flex items-center justify-center bg-[#3a1010] border border-[#b02418] text-[#f5b4b0] text-[10px] text-center px-2 leading-tight" data-testid={`missing-${p.id}`}>
                      CHÝBA OBRÁZOK<br/>MISSING IMAGE<br/><span className="opacity-70">re-upload alebo zmazať</span>
                    </div>
                  ) : (
                    <img src={resolveImg(p.image_url)} alt={p.title_sk} className="w-full h-32 object-cover" />
                  )}
                  <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => startEdit(p)} className="bg-black/70 p-2 rounded-full text-[#f5f2eb] hover:text-[#d97925]" data-testid={`edit-${p.id}`} title="Upraviť / Edit">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => del(p.id)} className="bg-black/70 p-2 rounded-full text-[#b02418] hover:text-red-400" data-testid={`delete-${p.id}`} title="Odstrániť / Delete">
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <div className="absolute bottom-9 left-2 right-2 flex justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="bg-black/70 p-1.5 rounded-full text-[#f5f2eb] disabled:opacity-30" data-testid={`move-left-${p.id}`} title="Posunúť vľavo">
                      <ChevronLeft size={14} />
                    </button>
                    <button onClick={() => move(i, 1)} disabled={i === paintings.length - 1} className="bg-black/70 p-1.5 rounded-full text-[#f5f2eb] disabled:opacity-30" data-testid={`move-right-${p.id}`} title="Posunúť vpravo">
                      <ChevronRight size={14} />
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-[#8d8a83] truncate">{p.title_sk || "—"}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
